/* src/stores/useBonusStore.js */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/axios'
import dayjs from 'dayjs'
import { useNotificationStore } from '@/stores/useNotificationStore'

export const useBonusStore = defineStore('bonus', () => {
  const bonuses = ref([])
  const now = ref(dayjs())
  let tickId = null

  // ─── FETCH FREEBET BONUSES ───────────────────────────────────────
  const fetchBonuses = async () => {
    const notificationStore = useNotificationStore()

    try {
      const response = await apiClient.get('/ds/v1/freebets')

      // API Response Structure:
      // {
      //   code: "Success",
      //   data: {
      //     code: 200,
      //     message: "...",
      //     data: {
      //       result: [...]
      //     }
      //   }
      // }
      const { code, data } = response.data

      if (code === 'Success' && data.code === 200 && Array.isArray(data.data.result)) {
        // Map in expiry calculations
        bonuses.value = data.data.result.map((b) => {
          const expiry = dayjs(b.expiry_date)
          return {
            ...b,
            expiry: expiry,
            totalMs: expiry.diff(now.value),
          }
        })
      } else if (code === 'Error' || data?.code === 404) {
        // No freebets
        bonuses.value = []
      } else {
        // Unexpected response structure
        console.warn('Unexpected bonus response structure:', response.data)
      }
    } catch (err) {
      // If there's no HTTP response, it's almost certainly a 401
      // already handled by your interceptor → bail out silently
      if (!err.response) {
        return
      }

      // Real HTTP error
      if (err.response.status !== 401) {
        notificationStore.showNotification(
          err.response.data?.data?.message ||
          err.response.data?.message ||
          err.message ||
          'Error loading bonuses. Please try again.',
          'error',
        )
      }
      // else 401: interceptor already logged out & redirected to Login
    }
  }

  // ─── SET BONUSES FROM LOGIN RESPONSE ─────────────────────────────
  const setBonusesFromLogin = (bonusData) => {
    if (Array.isArray(bonusData)) {
      bonuses.value = bonusData.map((b) => {
        const expiry = dayjs(b.expiry_date)
        return {
          ...b,
          expiry: expiry,
          totalMs: expiry.diff(now.value),
        }
      })
    }
  }

  // ─── DERIVED LISTS ───────────────────────────────────────────────
  const awardedBonuses = computed(() => bonuses.value.filter((b) => String(b.status) === '1'))
  const pendingBonuses = computed(() => bonuses.value.filter((b) => String(b.status) === '7'))
  const appliedBonuses = computed(() => bonuses.value.filter((b) => String(b.status) === '2'))
  const expiredBonuses = computed(() =>
    bonuses.value.filter((b) => !['1', '2', '7'].includes(String(b.status))),
  )
  const hasFreebet = computed(() => awardedBonuses.value.length > 0)

  // ─── REAL-TIME TICKER ────────────────────────────────────────────
  const startTicker = () => {
    if (tickId) clearInterval(tickId)
    tickId = setInterval(() => {
      now.value = dayjs()
    }, 1000)
  }
  const stopTicker = () => {
    if (tickId) {
      clearInterval(tickId)
      tickId = null
    }
  }

  return {
    bonuses,
    now,
    fetchBonuses,
    setBonusesFromLogin,
    awardedBonuses,
    pendingBonuses,
    appliedBonuses,
    expiredBonuses,
    hasFreebet,
    startTicker,
    stopTicker,
  }
})
